import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent, SlideshowSelectEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  /** @type {number | null} */
  #playTimeout = null;
  /** @type {number | null} */
  #pauseTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    // Pause this media if another media element starts playing
    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      (event) => {
        if (event.target !== this && this.isPlaying) {
          this.pauseMedia();
        }
      },
      { signal }
    );
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    document.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect.bind(this), { signal });

    // Setup intersection observer to autoplay when video enters viewport (e.g. grid mode or initial view)
    this.#setupIntersectionObserver();

    // Check visibility on initial render
    this.#checkInitialVisibility();
  }

  #setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const mySlide = this.closest('slideshow-slide');
          if (mySlide) {
            const isHidden = mySlide.getAttribute('aria-hidden') === 'true';
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6 && !isHidden) {
              this.playMedia();
            }
          } else {
            // Standalone or grid layout: autoplay when visible on screen, pause when offscreen
            if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
              this.playMedia();
            } else if (!entry.isIntersecting || entry.intersectionRatio < 0.1) {
              if (this.isPlaying) {
                this.pauseMedia();
              }
            }
          }
        }
      },
      {
        threshold: [0, 0.2, 0.5, 0.8],
      }
    );

    this.#intersectionObserver.observe(this);
  }

  #checkInitialVisibility() {
    const check = () => {
      if (!this.isConnected) return;

      // Ignore elements hidden via responsive CSS (e.g. desktop grid vs mobile carousel)
      if (this.offsetParent === null && window.getComputedStyle(this).display === 'none') {
        return;
      }

      const mySlide = this.closest('slideshow-slide');
      if (mySlide) {
        const isHidden = mySlide.getAttribute('aria-hidden') === 'true';
        if (!isHidden) {
          this.playMedia();
        }
      } else {
        const rect = this.getBoundingClientRect();
        const isInViewport =
          rect.top < window.innerHeight &&
          rect.bottom > 0 &&
          rect.left < window.innerWidth &&
          rect.right > 0;
        if (isInViewport) {
          this.playMedia();
        }
      }
    };

    requestAnimationFrame(() => {
      check();
      setTimeout(check, 350);
    });
  }

  #handleSlideshowSelect(event) {
    const mySlide = this.closest('slideshow-slide');
    if (!mySlide) return;

    const selectedSlide = event.detail?.slide;
    const isCurrentSlide = selectedSlide === mySlide || mySlide.getAttribute('aria-hidden') === 'false';

    if (isCurrentSlide) {
      if (this.#pauseTimeout) {
        clearTimeout(this.#pauseTimeout);
        this.#pauseTimeout = null;
      }

      if (this.#playTimeout) {
        clearTimeout(this.#playTimeout);
      }

      // Allow the slide transition to settle, then play
      this.#playTimeout = setTimeout(() => {
        if (mySlide.getAttribute('aria-hidden') !== 'true') {
          this.playMedia();
        }
      }, 180);
    } else {
      if (this.#playTimeout) {
        clearTimeout(this.#playTimeout);
        this.#playTimeout = null;
      }

      if (this.#pauseTimeout) {
        clearTimeout(this.#pauseTimeout);
      }

      // Only pause if this slide is confirmed hidden after transition finishes
      this.#pauseTimeout = setTimeout(() => {
        if (mySlide.getAttribute('aria-hidden') === 'true' && this.isPlaying) {
          this.pauseMedia();
        }
      }, 250);
    }
  }

  #bindVideoEvents(video) {
    if (!video || video._eventsBound) return;
    video._eventsBound = true;
    const signal = this.#abortController.signal;
    video.addEventListener(
      'play',
      () => {
        this.isPlaying = true;
        this.dispatchEvent(new MediaStartedPlayingEvent(this));
        this.updatePlayPauseHint(this.isPlaying);
      },
      { signal }
    );
    video.addEventListener(
      'pause',
      () => {
        this.isPlaying = false;
        this.updatePlayPauseHint(this.isPlaying);
      },
      { signal }
    );
  }

  #bindIframeEvents(iframe) {
    if (!iframe || iframe._eventsBound) return;
    iframe._eventsBound = true;

    iframe.addEventListener(
      'load',
      () => {
        if (this.isPlaying) {
          this.#sendIframeMessage(iframe, 'play');
        }
      },
      { once: true }
    );
  }

  #sendIframeMessage(iframe, action) {
    if (!iframe || !iframe.contentWindow) return;
    const videoType = iframe.dataset.videoType;

    try {
      if (videoType === 'youtube') {
        if (action === 'play') {
          iframe.contentWindow.postMessage('{"event":"command","func":"mute","args":""}', '*');
          iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        } else {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        }
      } else if (videoType === 'vimeo') {
        if (action === 'play') {
          iframe.contentWindow.postMessage('{"method":"setMuted","value":true}', '*');
          iframe.contentWindow.postMessage('{"method":"play"}', '*');
        } else {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      }
    } catch (e) {
      console.warn('Iframe postMessage error:', e);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
    if (this.#playTimeout) clearTimeout(this.#playTimeout);
    if (this.#pauseTimeout) clearTimeout(this.#pauseTimeout);
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content and plays it
   */
  showDeferredMedia = () => {
    this.playMedia();
  };

  /**
   * Loads the content into the DOM
   * @param {boolean} [focus=false] - Whether to focus the content
   */
  loadContent(focus = false) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    const posterButton = this.querySelector('.deferred-media__poster-button');
    posterButton?.classList.add('deferred-media__playing');

    const productMedia = this.closest('.product-media');
    if (productMedia) {
      productMedia.classList.add('media-is-playing');
    }

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    if (content instanceof HTMLVideoElement) {
      content.muted = true;
      content.defaultMuted = true;
      content.playsInline = true;
      content.autoplay = true;
      content.setAttribute('playsinline', '');
      content.setAttribute('muted', '');
      content.setAttribute('autoplay', '');
      this.#bindVideoEvents(content);
    } else if (content instanceof HTMLIFrameElement) {
      this.#bindIframeEvents(content);
    }
  }

  /**
   * Plays the media
   */
  playMedia() {
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);

    if (!this.getAttribute('data-media-loaded')) {
      this.loadContent(false);
    }

    const posterButton = this.querySelector('.deferred-media__poster-button');
    posterButton?.classList.add('deferred-media__playing');

    const productMedia = this.closest('.product-media');
    if (productMedia) {
      productMedia.classList.add('media-is-playing');
    }

    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      this.#sendIframeMessage(iframe, 'play');
      setTimeout(() => this.#sendIframeMessage(iframe, 'play'), 250);
      setTimeout(() => this.#sendIframeMessage(iframe, 'play'), 600);
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.autoplay = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.setAttribute('autoplay', '');
        this.#bindVideoEvents(video);

        const mySlide = this.closest('slideshow-slide');
        const handlePlayError = (err) => {
          if (err && err.name === 'AbortError') {
            // Scroll transition interrupted playback, retry once settled
            setTimeout(() => {
              if (this.isPlaying && (!mySlide || mySlide.getAttribute('aria-hidden') !== 'true')) {
                video.play()?.catch(() => {});
              }
            }, 300);
          } else {
            console.warn('Video play was prevented:', err);
          }
        };

        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(handlePlayError);
        }
      }
    }

    this.dispatchEvent(new MediaStartedPlayingEvent(this));
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    this.isPlaying = false;
    this.updatePlayPauseHint(this.isPlaying);

    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      this.#sendIframeMessage(iframe, 'pause');
    } else {
      this.querySelector('video')?.pause();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
